<template>
  <Dialog v-model:open="isOpen">
    <DialogContent class="max-w-xl">
      <DialogHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
        <DialogTitle class="text-lg font-semibold">{{ t('editSavedSnippet') }}</DialogTitle>
      </DialogHeader>
      <div class="space-y-2">
        <!-- Title Section -->
        <div class="space-y-1">
          <div class="flex items-center justify-between">
            <span class="text-sm font-medium text-gray-900">{{ t('title') }}</span>
            <button
              type="button"
              class="inline-flex items-center gap-1 rounded-md text-xs font-medium text-purple-600 hover:text-purple-700
                disabled:cursor-not-allowed disabled:opacity-50"
              :disabled="isSaving || isGeneratingTitle || !snippetContent"
              @click="generateTitle"
            >
              <span
                v-if="isGeneratingTitle"
                class="animate-spin"
              >
                <LoaderCircle class="h-3 w-3" />
              </span>
              <span v-else>
                <RotateCw class="h-3 w-3" />
              </span>
              {{ t('generateWithAI') }}
            </button>
          </div>
          <InputField
            v-model="snippetTitle"
            name="snippet-title"
            :placeholder="t('snippetTitlePlaceholder')"
            :rules="{ required: true, max: 50 }"
          />
          <div class="text-right text-xs text-gray-500">
            {{ snippetTitle.length }}/50 characters
          </div>
        </div>
        <!-- Content Section -->
        <div class="space-y-1">
          <TextAreaField
            v-model="snippetContent"
            name="snippet-content"
            :label="t('content')"
            rows="12"
            :rules="yup.string().required()"
            :disabled="false"
          />
        </div>
      </div>

      <!-- Footer -->
      <div class="flex gap-2 pt-3">
        <button
          class="flex-1 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700
            hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
          @click="closeDialog"
        >
          {{ t('cancel') }}
        </button>
        <button
          :disabled="!isValid || isSaving"
          class="inline-flex flex-1 items-center justify-center rounded-md bg-purple-600 px-4 py-2 text-sm
            font-medium text-white hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500
            focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          @click="saveSnippet"
        >
          <LoaderCircle
            v-if="isSaving"
            class="mr-2 h-4 w-4 animate-spin"
          />
          {{ t('save') }}
        </button>
      </div>
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
  import { ref, computed, watch, onMounted } from 'vue'
  import { useTranslation } from '@/composables/useTranslation'
  import { useToast } from '@/components/ui/toast/use-toast'
  import { LoaderCircle, RotateCw, X } from 'lucide-vue-next'
  import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
  import { SavedResponsesService } from '@/types/generated/services.gen'
  import InputField from '@/components/admin/form/InputField.vue'
  import TextAreaField from '@/components/admin/form/TextAreaField.vue'
  import * as yup from 'yup'

  const props = defineProps<{
    snippet: any
  }>()

  const emit = defineEmits(['close', 'updated'])

  const { t } = useTranslation({
    en: {
      editSavedSnippet: 'Edit saved snippet',
      title: 'Title',
      snippetTitlePlaceholder: 'Saved snippet title',
      content: 'Content',
      cancel: 'Cancel',
      save: 'Save',
      snippetSaved: 'Snippet updated successfully',
      errorSaving: 'Error updating snippet',
      generateWithAI: 'Generate with AI',
      generatingTitle: 'Generating...',
      titleGenerationError: 'Error generating title'
    },
    de: {
      editSavedSnippet: 'Gespeicherten Snippet bearbeiten',
      title: 'Titel',
      snippetTitlePlaceholder: 'Gespeicherter Snippet Titel',
      content: 'Inhalt',
      cancel: 'Abbrechen',
      save: 'Speichern',
      snippetSaved: 'Snippet erfolgreich aktualisiert',
      errorSaving: 'Fehler beim Aktualisieren des Snippets',
      generateWithAI: 'Mit KI generieren',
      generatingTitle: 'Generiere...',
      titleGenerationError: 'Fehler beim Generieren des Titels'
    }
  })

  const { toast } = useToast()

  // Initialize with true so it's open when mounted
  const isOpen = ref(true)
  const snippetTitle = ref('')
  const snippetContent = ref('')
  const isSaving = ref(false)
  const isGeneratingTitle = ref(false)

  const isValid = computed(() => snippetTitle.value.trim().length > 0)

  // Initialize form with snippet data
  onMounted(() => {
    if (props.snippet) {
      console.log('Snippet data:', props.snippet)
      snippetTitle.value = props.snippet.title || ''
      snippetContent.value = props.snippet.content || ''
    }
  })

  // Watch for dialog close event (when clicking outside)
  watch(isOpen, (newValue) => {
    if (!newValue) {
      emit('close')
    }
  })

  const closeDialog = () => {
    isOpen.value = false
  }

  const generateTitle = async () => {
    if (!snippetContent.value || isGeneratingTitle.value) return

    isGeneratingTitle.value = true
    snippetTitle.value = ''

    try {
      const response = await SavedResponsesService.savedResponsesGenerateTitleCreate({
        requestBody: {
          content: snippetContent.value
        }
      })

      // Simulate streaming effect for better UX
      const title = response.title
      let currentIndex = 0

      const streamInterval = setInterval(() => {
        if (currentIndex < title.length) {
          snippetTitle.value += title[currentIndex]
          currentIndex++
        } else {
          clearInterval(streamInterval)
          isGeneratingTitle.value = false
        }
      }, 15)
    } catch (error) {
      console.error('Error generating title:', error)
      toast({
        title: t('titleGenerationError'),
        variant: 'destructive'
      })
      isGeneratingTitle.value = false
    }
  }

  const saveSnippet = async () => {
    if (!isValid.value || !props.snippet?.uuid) return

    isSaving.value = true

    try {
      await SavedResponsesService.savedResponsesPartialUpdate({
        uuid: props.snippet.uuid,
        requestBody: {
          title: snippetTitle.value.trim(),
          content: snippetContent.value
        }
      })

      toast({
        title: t('snippetSaved'),
        variant: 'success'
      })

      emit('updated')
      closeDialog()
    } catch (error) {
      console.error('Error updating snippet:', error)
      toast({
        title: t('errorSaving'),
        variant: 'destructive'
      })
    } finally {
      isSaving.value = false
    }
  }
</script>
